import { emitEvent } from '@/utils/eventBus';
import { useAccountStore } from '@/stores/user';
// import { useReport } from '@/utils/hooks/useReport';
import { checkEnterpriseMember, checkEnterpriseInfo } from '@/utils/routerNav';
import { LoginConfig } from '@/constant/login';
import { useGlobalInfoStore } from '@/stores/Global';
import { enterpriseInfoStore } from '@/stores/enterprise';
import type { Router } from 'vue-router';
import { GOTO_QUICK_LOGIN } from '@/constant';
import { savePageRef, extractAuthStateInfo } from '@/utils';

let currentEnterpriseId = ''; // 当前企业ID

/**
 * 重置HTTP状态
 * @description 切换路由时重置全局状态，防止一直上报错误状态码
 */
function resetHttpStatus() {
  const globalStore = useGlobalInfoStore();
  globalStore.httpStatus !== 200 && globalStore.setHttpStatus(200);
}

// 企业进入控制
async function enterEnterprise(to: any, from: any, router: any) {
  const account = useAccountStore();
  await account.checkIsLogin();
  const n = to.params?.namespace;
  const pm = to.params?.pathMatch;
  const namespace =
    n != null && n !== ''
      ? Array.isArray(n)
        ? String(n[0])
        : String(n)
      : Array.isArray(pm) && pm[0]
        ? String(pm[0])
        : typeof pm === 'string' && pm
          ? pm.split('/')[0]
          : undefined;
  const haveInitEnterprise: boolean = namespace === currentEnterpriseId;
  const enterpriseStore = enterpriseInfoStore();

  if (haveInitEnterprise) {
    void checkEnterpriseInfo(to, true);
  } else {
    const globalStore = useGlobalInfoStore();
    globalStore.setIsNotFound(false);
    globalStore.setIsNoAccess(false);
    globalStore.setIsPendingReview(false);

    if (namespace) {
      // 与界面渲染并行：不阻塞路由；Promise.all 并行拉企业信息与成员权限，deferSetFinished 避免闪错页
      void Promise.all([
        checkEnterpriseInfo(to, false, { deferSetFinished: true }),
        checkEnterpriseMember(namespace, to)
      ]).finally(() => {
        enterpriseStore.setEnterpriseFinished(true);
      });
    } else {
      void checkEnterpriseInfo(to, false);
    }
  }

  currentEnterpriseId = namespace as string;
  needLoginCheck(to, router);
}

// 界面是否需要登录
function needLoginCheck(to: any, router: any) {
  const account = useAccountStore();
  // 验证登录权限
  if (to.meta.needLogin) {
    // 是否需要登录
    if (account.isLogin) {
      // 已登录
      return true;
    } else {
      emitEvent('needLogin', {
        triggerType: to.meta?.loginType,
        Authorization: Boolean(to?.meta?.Authorization),
        loginTriggerSource: 'router_login_auth'
      });
      return false;
    }
  } else {
    if (account.isLogin && to.meta.loginHidden) {
      // 已登录，不能访问某些页面，进行跳转处理（如：登录注册）
      const { stateFrom } = extractAuthStateInfo(to.query?.state);
      const toOauth = to.name === 'oauth' && stateFrom; // 三方授权不跳转
      const fromAtomGit = to.query.installation_id && /atomgit.com/.test(document.referrer); // 来自AtomGit应用的跳转
      if (toOauth || fromAtomGit) return true;
      // 跳转到当前根域名
      location.href = window.location.origin;
      return false;
    }
  }
}

export const injectRouter = (router: Router) => {
  router.beforeEach(async (to, from) => {
    // ----------------------------- 通用前置检查 -----------------------------

    // 1. 页面内操作检查：同一路由且携带 query 参数，无需重复请求
    if (to.name === from.name && Object.keys(to.query).length > 0) {
      return true;
    }

    // ----------------------------- 站点状态和初始化处理 -----------------------------
    resetHttpStatus();

    // 上报 ref 设置
    savePageRef(to.fullPath, typeof from?.name === 'string' ? from.name : '');

    // ----------------------------- 特殊路由处理 -----------------------------

    // 快捷登录检查
    const needQuickLogin = await LoginConfig.beforeCsdnQuickLogin({ route: to });
    if (needQuickLogin) {
      return false;
    }

    // ----------------------------- 页面访问控制和数据初始化 -----------------------------
    // 获取企业信息(并存储到store中)；与导航、界面并行，不在此 await
    enterEnterprise(to, from, router);
    return true;
  });

  // ----------------------------- 辅助函数 -----------------------------

  router.afterEach((to: any, from) => {
    if (to.fullPath === '/' || to.name !== from.name) {
      if (sessionStorage.getItem(GOTO_QUICK_LOGIN)) {
        sessionStorage.removeItem(GOTO_QUICK_LOGIN);
        return false;
      }
    }
  });
};
